import React, { lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { Toaster } from "./components/ui/toaster";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import CookieConsent from "./components/CookieConsent";
import HomePage from "./pages/HomePage";
import PropertiesPage from "./pages/PropertiesPage";
import PropertyDetailPage from "./pages/PropertyDetailPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import SavedPropertiesPage from "./pages/SavedPropertiesPage";
import CookiePreferencesPage from "./pages/CookiePreferencesPage";
import TermsOfUsePage from "./pages/TermsOfUsePage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import LegalPage from "./pages/LegalPage";
import AboutPage from "./pages/AboutPage";
import CareersPage from "./pages/CareersPage";
import HelpPage from "./pages/HelpPage";
import AgentDetailPage from "./pages/AgentDetailPage";

// Lazy load Studio to reduce bundle size
const StudioPage = lazy(() => import("./pages/StudioPage"));

// Layout wrapper that hides Navbar/Footer for admin routes
const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  if (isAdminRoute) {
    return <>{children}</>;
  }

  return (
    <div className="App">
      <Navbar />
      {children}
      <Toaster />
      <CookieConsent />
      <Footer />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppLayout>
            <Routes>
              {/* Admin/Studio route */}
              <Route
                path="/admin/*"
                element={
                  <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading Studio...</div>}>
                    <StudioPage />
                  </Suspense>
                }
              />
              {/* Redirect default locale to Spanish */}
              <Route path="/" element={<Navigate to="/es" replace />} />

              {/* Spanish routes */}
              <Route path="/es" element={<HomePage />} />
              <Route path="/es/propiedades" element={<PropertiesPage />} />
              <Route path="/es/propiedad/:id" element={<PropertyDetailPage />} />
              <Route path="/es/login" element={<LoginPage />} />
              <Route path="/es/signup" element={<SignupPage />} />
              <Route path="/es/guardadas" element={<SavedPropertiesPage />} />
              <Route path="/es/cookies" element={<CookiePreferencesPage />} />
              <Route path="/es/terminos" element={<TermsOfUsePage />} />
              <Route path="/es/privacidad" element={<PrivacyPolicyPage />} />
              <Route path="/es/legal" element={<LegalPage />} />
              <Route path="/es/acerca-de" element={<AboutPage />} />
              <Route path="/es/trabajos" element={<CareersPage />} />
              <Route path="/es/ayuda" element={<HelpPage />} />
              <Route path="/es/agente/:id" element={<AgentDetailPage />} />

              {/* English routes */}
              <Route path="/en" element={<HomePage />} />
              <Route path="/en/properties" element={<PropertiesPage />} />
              <Route path="/en/property/:id" element={<PropertyDetailPage />} />
              <Route path="/en/login" element={<LoginPage />} />
              <Route path="/en/signup" element={<SignupPage />} />
              <Route path="/en/saved" element={<SavedPropertiesPage />} />
              <Route path="/en/cookies" element={<CookiePreferencesPage />} />
              <Route path="/en/terms" element={<TermsOfUsePage />} />
              <Route path="/en/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/en/legal" element={<LegalPage />} />
              <Route path="/en/about" element={<AboutPage />} />
              <Route path="/en/careers" element={<CareersPage />} />
              <Route path="/en/help" element={<HelpPage />} />
              <Route path="/en/agent/:id" element={<AgentDetailPage />} />

              {/* Fallback redirect to Spanish */}
              <Route path="*" element={<Navigate to="/es" replace />} />
            </Routes>
          </AppLayout>
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
};

export default App;
